import Modal from "@components/Modal";
import UpdateJobStages from "./UpdateJobStages";
import { Link, useLocation } from "react-router-dom";
import { useAuthContext } from "@contexts/auth";
import { useDashboardUIActions, useDashboardUIContext } from "@contexts/dashboardUI";
import { useProjectActions, useProjectContext } from "@contexts/projects";
import { formatDate, formatDateWithTime } from "@utils/formatDate";
import { useEffect, useRef, useState } from "react";
import Tooltip from "./tooltip";
import { useBimContext } from "@contexts/bim";
import { useCostsContext } from "@contexts/costs";
import { checkIfMultipleIds } from "@utils/checkMultipleIds";
import { downloadCsv } from "@utils/downloadCsv";
import { trackEvent, trackReportDownload } from "@utils/activityTracker";
import { usePlanBasis } from "@utils/planBasis";
import PresentationModeBadge from "./PresentationModeBadge";
import PresentationMutationGate from "./PresentationMutationGate";
import { canRefreshPrevision } from "@utils/presentationMode";
import {
  formatSiengeQuotaMessage,
  isSiengeQuotaBlocked,
  isUpdateJobActive,
  partialExtractionMessage,
  resolveProjectUpdateTypes,
  SIENGE_QUOTA_ERROR_CODE,
  updateJobMessage,
} from "@contexts/projects/updateJobs";

// `failed`/`blocked` já eram erro. 21/09: uma atualização pode terminar bem e ainda ter
// rotas negadas pelo ERP — mostrar isso em verde de sucesso faria o usuário ler o dado
// mantido do download anterior como recém-atualizado.
const updateAlertVariant = (job) => {
  if (["failed", "blocked"].includes(job?.status)) return "error";
  return partialExtractionMessage(job) ? "info" : "success";
};

// Rótulo do histórico de atualizações — pelo `type` gravado no log
// (`companies.updateLogs[].type`), não pelos créditos de cota consumidos. `trend`,
// `purchase_orders` e `prevision` consomem 0 créditos cada (ver `UPDATE_CONSUMPTION` no
// back-end): inferir o rótulo a partir do consumo fazia toda atualização de Pedidos e
// Contratos MEGA aparecer como "Tendência" no histórico, porque as duas batiam em 0.
const UPDATE_LOG_TYPE_LABELS = {
  complete: "Completo",
  disbursement: "Desembolso",
  trend: "Tendência",
  financial: "Financeiro",
  stock: "Estoque",
  prevision: "Prevision",
};

const updateLogTypeLabel = (type, erp) => {
  if (type === "purchase_orders") return purchaseOrdersLabel(erp);
  return UPDATE_LOG_TYPE_LABELS[type] || type || "";
};

// Rótulo e descrição de cada atualização — visíveis sem hover (a única fonte era `title`, que
// não aparece em toque e é fácil de não descobrir). O texto reflete o recorte REAL de cada
// ERP: em Sienge, "Pedidos" é uma janela recente; em MEGA a mesma ação recarrega o histórico
// inteiro de pedidos e os contratos por inteiro -- a rota de contratos MEGA
// (`extractContracts`) não aceita parâmetro de data, e o saldo dos pedidos alimenta a
// Tendência por composição (uma janela deixaria velho o saldo de pedido antigo recebido).
const isMegaErp = (erp) => String(erp || "").toUpperCase() === "MEGA";

const purchaseOrdersLabel = (erp) => (isMegaErp(erp) ? "Pedidos e Contratos" : "Pedidos");

const MEGA_PURCHASE_ORDERS_DESCRIPTION = "Atualiza todo o histórico de pedidos e contratos da obra.";

// O período do recorte de Pedidos era fixo em "últimos 7 dias" (hardcoded), sem opção de
// ampliar — obra recém-migrada, ou com um hiato de sincronização, ficava sem jeito de puxar
// pedidos mais antigos pelo botão (só um FULL, que re-extrai orçamento/medições/financeiro
// junto). O back-end já validava qualquer janela de até 92 dias
// (`updateJobs.js::normalizeUpdatePeriod`); só faltava a UI deixar escolher.
const MAX_PURCHASE_ORDERS_PERIOD_DAYS = 92;

const isoDate = (date) => date.toISOString().slice(0, 10);

const defaultPurchaseOrdersPeriod = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 7);
  return { start_date: isoDate(start), end_date: isoDate(end) };
};

// Mesma regra de `normalizeUpdatePeriod` no back-end — valida aqui só pra desabilitar o botão
// e explicar o motivo antes de gastar uma requisição; a validação de verdade continua lá.
const purchaseOrdersPeriodError = (period) => {
  const start = new Date(`${period.start_date}T00:00:00.000Z`);
  const end = new Date(`${period.end_date}T00:00:00.000Z`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return "Datas inválidas.";
  }
  const days = (end.getTime() - start.getTime()) / 86_400_000;
  if (days < 0) return "A data final precisa ser depois da inicial.";
  if (days > MAX_PURCHASE_ORDERS_PERIOD_DAYS) {
    return `O período não pode passar de ${MAX_PURCHASE_ORDERS_PERIOD_DAYS} dias.`;
  }
  return null;
};

// "Obra inteira" (Pedidos do Sienge, D12): o FE manda só `{ scope: "whole_work" }` e o
// back-end resolve do `start_date` do cadastro até hoje, em janelas de até 92 dias no
// pipeline. A data de início vem do `/detail` em ISO; lida em UTC para não voltar um dia.
const WHOLE_WORK_PERIOD = Object.freeze({ scope: "whole_work" });

const formatFullDate = (date) => [
  String(date.getUTCDate()).padStart(2, "0"),
  String(date.getUTCMonth() + 1).padStart(2, "0"),
  date.getUTCFullYear(),
].join("/");

const workStartDate = (project) => {
  if (!project?.start_date) return null;
  const date = new Date(project.start_date);
  return Number.isNaN(date.getTime()) ? null : date;
};

const todayLabel = () => {
  const now = new Date();
  return formatFullDate(new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())));
};

// Campos do cadastro que a atualização de Pedidos e Contratos MEGA exige (C6).
const REGISTRATION_FIELD_LABELS = {
  cnpj: "CNPJ da obra",
  projeto_reduzido: "código reduzido do projeto",
};

const joinFieldLabels = (fields) => {
  const labels = fields.map((field) => REGISTRATION_FIELD_LABELS[field] ?? field);
  return labels.length > 1
    ? `${labels.slice(0, -1).join(", ")} e ${labels[labels.length - 1]}`
    : labels[0];
};

const missingRegistrationMessage = (fields) => (
  Array.isArray(fields) && fields.length > 0
    ? `Falta no cadastro da obra: ${joinFieldLabels(fields)}. Sem isso, a atualização de Pedidos e Contratos não é oferecida.`
    : null
);

// Cadastro de obras do Backoffice (só para quem tem acesso a ele, como na barra lateral).
const BUILDING_REGISTRATION_PATH = "/dashboard/backoffice/companies-buildings";

const formatPeriodRange = (period) => {
  const [, sm, sd] = period.start_date.split("-");
  const [, em, ed] = period.end_date.split("-");
  return `${sd}/${sm} a ${ed}/${em}`;
};

// Lista de títulos anulados mudou e ainda não foi aplicada (`titleAnnulments.pending`): só o
// Financeiro e o Completo aplicam a lista; as demais atualizações dizem que não.
export const ANNULMENTS_FINANCIAL_REASON = "A lista de títulos anulados mudou. Os valores de pago e a pagar só refletem a lista depois desta atualização.";
export const ANNULMENTS_COMPLETE_NOTE = "Também aplica a lista de títulos anulados.";
export const ANNULMENTS_NOT_APPLIED_NOTE = "Não aplica a lista de títulos anulados.";

const updateActionDescription = (type, { erp, hasStock, purchaseOrdersPeriod, wholeWorkStart, annulmentsPending } = {}) => {
  const base = baseUpdateActionDescription(type, { erp, hasStock, purchaseOrdersPeriod, wholeWorkStart });
  if (!annulmentsPending || !base) return base;
  if (type === "complete") return `${base} ${ANNULMENTS_COMPLETE_NOTE}`;
  if (type === "financial") return base;
  return `${base} ${ANNULMENTS_NOT_APPLIED_NOTE}`;
};

const baseUpdateActionDescription = (type, { erp, hasStock, purchaseOrdersPeriod, wholeWorkStart } = {}) => {
  switch (type) {
    case "complete":
      return hasStock
        ? "Refaz orçamento, medições e financeiro da obra inteira (sem estoque)."
        : "Refaz orçamento, medições, financeiro e estoque da obra inteira.";
    case "trend":
      return "Atualiza a tendência de custo da obra.";
    case "financial":
      return "Atualiza contas a pagar e a receber.";
    case "purchase_orders": {
      // MEGA (revisão de 07/10/2026): toda execução recarrega o histórico inteiro de pedidos
      // e de contratos -- o saldo do pedido alimenta a Tendência por composição, e um pedido
      // antigo recebido depois de uma janela ficaria com saldo velho. Não há período a
      // escolher, então a descrição não fala de janela nenhuma.
      if (isMegaErp(erp)) {
        return MEGA_PURCHASE_ORDERS_DESCRIPTION;
      }
      if (purchaseOrdersPeriod?.scope === WHOLE_WORK_PERIOD.scope) {
        return wholeWorkStart
          ? `Atualiza os pedidos de ${formatFullDate(wholeWorkStart)} a ${todayLabel()}.`
          : "Atualiza os pedidos da data de início da obra até hoje.";
      }
      const range = purchaseOrdersPeriod ? formatPeriodRange(purchaseOrdersPeriod) : "período escolhido abaixo";
      return `Atualiza os pedidos de ${range}.`;
    }
    case "stock":
      return "Atualiza o estoque da obra.";
    case "prevision":
      return "Atualiza Previsto, Medido e Linha de Base do Prevision.";
    default:
      return null;
  }
};

// Motivo de indisponibilidade vindo do back-end (`updateCapabilities.unavailable_reasons`) —
// hoje só cobre cadastro incompleto de Pedidos e Contratos MEGA (`missing_registration_fields`),
// mas o formato já é por tipo para acomodar outros motivos sem mudar o contrato.
const unavailableReasonFor = (activeProject, type) => (
  (Array.isArray(activeProject?.updateCapabilities?.unavailable_reasons)
    ? activeProject.updateCapabilities.unavailable_reasons
    : []
  ).find((reason) => reason?.type === type) ?? null
);

const HEADER_POLL_MS = 10000
const HEADER_POLL_BACKOFF_MS = 30000
const HEADER_POLL_FAILURE_NOTICE = 5

const Header = () => {
  const { user } = useAuthContext()
  const {
    activeProject,
    companies,
    cubBased,
    forecastBased,
    updateJobsByBuilding,
  } = useProjectContext();
  const { openSidebar, toggleOffcanvas, toggleHelpModal } = useDashboardUIActions();
  const {
    createUpdateJob,
    getUpdateJob,
    updateCubBased,
    updateForecastBased,
  } = useProjectActions();
  const { hasBothPlanBases } = usePlanBasis();
  const { showSidebar } = useDashboardUIContext();
  const { currentProject } = useBimContext()
  const { currentBuilding } = useCostsContext()
  const [modalVisible, setModalVisible] = useState(false)
  const { pathname } = useLocation();
  const [response, setResponse] = useState({
    done: false,
    ok: false,
    responseHead: '',
    responseText: '',
  })
  const [seeAllBuildingUpdates, setSeeAllBuildingUpdates] = useState(false);
  const [seeAllCompanyUpdates, setSeeAllCompanyUpdates] = useState(false);
  const [requestingUpdate, setRequestingUpdate] = useState(false);
  const [purchaseOrdersPeriod, setPurchaseOrdersPeriod] = useState(defaultPurchaseOrdersPeriod);
  // `window`: a janela escolhida (até 92 dias); `whole_work`: da data de início até hoje.
  const [purchaseOrdersScope, setPurchaseOrdersScope] = useState("window");
  const [updatePollIssue, setUpdatePollIssue] = useState(false);
  const updatePollIssueRef = useRef(false);
  // Cota diária da API Sienge (Task 14): o BE (`GET /project/updateJob`, PR #119) devolve
  // `sienge_quota_blocked_until` no TOPO do payload de status (não dentro de `job` — o
  // `serializeUpdateJob` do BE não repassa esse campo para o job) e, depois do pipeline #14A
  // gravar em `companies`, também deve aparecer na listagem de construtoras; cobrimos os dois
  // lugares como fallback.
  const [polledQuotaBlockedUntil, setPolledQuotaBlockedUntil] = useState(null);
  const currentJob = updateJobsByBuilding?.[activeProject?.building_id];
  const [terminalJobId, setTerminalJobId] = useState(null);
  const updateInProgress = isUpdateJobActive(currentJob) && currentJob?.id !== terminalJobId;
  // Id do job cujo progresso está sendo acompanhado. Existe para o painel de etapas NÃO
  // desmontar no instante em que o job vira terminal: `updateInProgress` cai para false nesse
  // momento, e o desmonte impediria o callback terminal (o auto-refresh) de disparar.
  const [trackedJobId, setTrackedJobId] = useState(null);
  useEffect(() => {
    if (updateInProgress && currentJob?.id) setTrackedJobId(currentJob.id);
  }, [updateInProgress, currentJob?.id]);
  const showStages = !!currentJob?.id && currentJob.id === trackedJobId;
  const currentCompany = companies?.find(i => i._id === activeProject?.company_id)
  const availableUpdateTypes = resolveProjectUpdateTypes(activeProject, currentCompany?.erp);
  const quotaBlockedUntil = polledQuotaBlockedUntil ?? currentCompany?.sienge_quota_blocked_until ?? null;
  const quotaBlocked = isSiengeQuotaBlocked(quotaBlockedUntil);

  useEffect(() => {
    const buildingId = activeProject?.building_id;
    if (!buildingId) return undefined;
    let cancelled = false;
    let timer;
    const knownActiveJob = isUpdateJobActive(currentJob);
    const setPollIssueVisible = (visible) => {
      if (updatePollIssueRef.current === visible) return;
      updatePollIssueRef.current = visible;
      setUpdatePollIssue(visible);
    };
    // Este laço governa só o rótulo do botão (ATUALIZAR / ATUALIZANDO). Ele continua tentando
    // após falha isolada: antes, uma única resposta não-ok o encerrava para sempre e o botão
    // ficava travado em "ATUALIZANDO" até recarregar a página.
    let failures = 0;
    const poll = async () => {
      let result;
      try {
        result = await getUpdateJob(buildingId);
      } catch {
        result = null;
      }
      if (cancelled) return;
      if (!result?.ok) {
        failures += 1;
        if (!knownActiveJob) return;
        if (failures >= HEADER_POLL_FAILURE_NOTICE) setPollIssueVisible(true);
        timer = setTimeout(
          poll,
          failures >= HEADER_POLL_FAILURE_NOTICE ? HEADER_POLL_BACKOFF_MS : HEADER_POLL_MS,
        );
        return;
      }
      failures = 0;
      setPollIssueVisible(false);
      // Cota Sienge (Task 14): o BE expõe `sienge_quota_blocked_until` no topo deste payload de
      // status (`serializeUpdateJob` não copia o campo para dentro de `job`).
      setPolledQuotaBlockedUntil(result?.body?.sienge_quota_blocked_until ?? null);
      if (isUpdateJobActive(result?.body?.job)) {
        timer = setTimeout(poll, HEADER_POLL_MS);
      } else if (knownActiveJob) {
        setTerminalJobId(result?.body?.job?.id ?? currentJob?.id ?? null);
      }
    };
    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [activeProject?.building_id, currentJob?.id]);

  const supportsUpdate = (type) => availableUpdateTypes.includes(type);
  const hasVisibleUpdateType = [
    "complete",
    "trend",
    "financial",
    "purchase_orders",
    "stock",
    "prevision",
  ].some((type) => (
    supportsUpdate(type)
    && (type !== "stock" || activeProject?.hasStock === true)
    && (type !== "prevision" || canRefreshPrevision(activeProject))
  ));

  const handleModal = () => {
    setResponse({ ...response, done: false })
    setModalVisible(!modalVisible)
  }

  const sendToUpdate = async (type, period) => {
    if (requestingUpdate || updateInProgress || quotaBlocked) return;
    setRequestingUpdate(true);
    // `purchase_orders` sempre manda um período explícito -- inclusive `null`, que pede o
    // histórico completo (só MEGA) -- porque `createUpdateJob(type)` sem segundo argumento
    // omitiria o campo, e o back-end trata período ausente como inválido pra esse tipo.
    const result = type === "purchase_orders"
      ? await createUpdateJob(type, period ?? null)
      : await createUpdateJob(type);
    const accepted = result?.ok || (result?.status === 409 && result?.body?.job);
    trackEvent("data_update_requested", {
      metadata: {
        updateType: type,
        fullHistory: type === "purchase_orders" && period === null,
        accepted: Boolean(accepted),
        status: result?.status,
      },
    });
    // Contrato Task 14: 429 { code: "sienge_quota_exceeded", message, retry_after } substitui a
    // mensagem genérica de erro pela específica de cota, e passa a bloquear os botões também.
    const quotaExceeded = result?.status === 429 && result?.body?.code === SIENGE_QUOTA_ERROR_CODE;
    if (quotaExceeded) setPolledQuotaBlockedUntil(result?.body?.retry_after ?? null);
    // Recusas tipadas do back-end (C6): explicadas pelo que falta no cadastro, não pela
    // mensagem crua do servidor.
    const refusalCode = result?.status === 422 ? result?.body?.code : null;
    const refusalText = refusalCode === "missing_start_date"
      ? "A obra não tem data de início cadastrada. Cadastre a data de início da obra para atualizar os pedidos da obra inteira."
      : refusalCode === "missing_registration_fields"
        ? missingRegistrationMessage(result?.body?.fields)
          ?? "Falta o CNPJ da obra ou o código reduzido do projeto no cadastro da obra."
        : null;
    setResponse({
      done: true,
      ok: Boolean(accepted),
      responseHead: accepted ? "Solicitação recebida" : "Erro",
      registrationLink: refusalCode === "missing_registration_fields" || refusalCode === "missing_start_date",
      responseText: quotaExceeded
        ? formatSiengeQuotaMessage(result?.body?.retry_after)
        : refusalText ?? result?.body?.message
          ?? (accepted
            ? updateJobMessage(result?.body?.job)
            : "Não foi possível iniciar a atualização. Tente novamente."),
    });
    setRequestingUpdate(false);
  }

  // Obra MEGA não escolhe período: a atualização sempre recarrega o histórico inteiro
  // (`period: null`, que o back-end já aceita em `normalizeUpdatePeriod`).
  const purchaseOrdersFullHistory = isMegaErp(currentCompany?.erp);
  const wholeWorkStart = workStartDate(activeProject);
  // Sem data de início a opção "Obra inteira" fica indisponível; uma escolha anterior volta
  // para a janela em vez de prometer um período que o back-end recusa.
  const purchaseOrdersWholeWork = !purchaseOrdersFullHistory
    && purchaseOrdersScope === "whole_work"
    && Boolean(wholeWorkStart);
  const purchaseOrdersPeriodInvalid = purchaseOrdersFullHistory || purchaseOrdersWholeWork
    ? null
    : purchaseOrdersPeriodError(purchaseOrdersPeriod);
  const canOpenRegistration = Boolean(user?.roles?.includes("admin"));
  // Lista de títulos anulados alterada e ainda não aplicada na obra (Sienge ou MEGA).
  const annulmentsPending = Boolean(activeProject?.titleAnnulments?.pending);
  const purchaseOrdersUnavailable = unavailableReasonFor(activeProject, "purchase_orders");

  const sendRecentPurchaseOrdersUpdate = () => {
    if (purchaseOrdersFullHistory) {
      sendToUpdate("purchase_orders", null);
      return;
    }
    if (purchaseOrdersWholeWork) {
      sendToUpdate("purchase_orders", WHOLE_WORK_PERIOD);
      return;
    }
    if (purchaseOrdersPeriodInvalid) return;
    sendToUpdate("purchase_orders", purchaseOrdersPeriod);
  };

  const handlePurchaseOrdersPeriodChange = (field) => (e) => {
    setPurchaseOrdersPeriod(prev => ({ ...prev, [field]: e.target.value }));
  };

  const formatHeader = () => {
    if (!user) return "Carregando..."
    const header = user?.configurations?.header
    return `${header?.id ? `${activeProject?.building_code} - ` : ""}${(activeProject?.custom_name ?? activeProject?.building_name) || "Carregando..."}`
  }

  const getHeader = () => {
    if (pathname.includes("bim")) return currentProject.name ?? "Selecione um projeto"
    if (pathname.includes("costs")) return currentBuilding.name ?? "Selecione um projeto"
    if (pathname.includes("settings")) return `Configurações - ${user?.email}`
    return formatHeader()
  }

  const getLastBuildingUpdate = () => {
    const buildingUpdates = currentCompany?.updateLogs?.filter(i => i?.building_name === activeProject?.building_name);
    if (!buildingUpdates) return "-"


    const updates = buildingUpdates.map(i => {
      if (!i.date) return;
      const time = new Date(i.date);
      time.setHours(time.getHours() - 3);
      const formattedTime = time?.toISOString().split('T')[1].split(':');
      return `${formatDate(i?.date)}, às ${formattedTime[0]}h${formattedTime[1]}m${i.email && `, por ${i.email}`} - ${updateLogTypeLabel(i.type, currentCompany?.erp)}`
    })
    return updates.reverse();
  }

  const getCompanyUpdates = () => {
    const buildingUpdates = currentCompany?.updateLogs
    const updates = buildingUpdates.map(i => {
      if (!i.date) return;
      const time = new Date(i.date);
      time.setHours(time.getHours() - 3);
      const formattedTime = time?.toISOString().split('T')[1].split(':');
      return `${formatDate(i?.date)}, às ${formattedTime[0]}h${formattedTime[1]}m${i.email ? `, por ${i.email}` : ""}, ${i.building_name ? i.building_name : ""} - ${updateLogTypeLabel(i.type, currentCompany?.erp)}`
    })
    return updates.reverse()
  }

  const handleSeeMoreBuilding = () => {
    setSeeAllBuildingUpdates(!seeAllBuildingUpdates);
  }

  const handleSeeMoreCompany = () => {
    setSeeAllCompanyUpdates(!seeAllCompanyUpdates);
  }

  const handleDownload = (e, type) => {
    let res = []
    const fields = [
      { label: 'Obra', key: 'building_name' },
      { label: 'Data', key: 'date' },
      { label: 'Usuário', key: 'email' },
      { label: 'Tipo', key: 'type' },
    ]

    if (type === 'building') {
      res = currentCompany?.updateLogs?.filter(i => i?.building_name === activeProject?.building_name);
    } else {
      res = currentCompany?.updateLogs
    }

    const form = res.map(i => {
      const time = new Date(i.date);
      time.setHours(time.getHours() - 3);
      const formattedTime = time?.toISOString().split('T')[1].split(':');
      return {
        building_name: i.building_name,
        date: `${formatDate(i.date)}, ${formattedTime[0]}h${formattedTime[1]}m`,
        email: i.email || '',
        type: updateLogTypeLabel(i.type, currentCompany?.erp) || ''
      }
    })

    trackReportDownload(`atualizacoes_${type === 'building' ? 'obra' : 'empresa'}`, { format: "xlsx" });
    downloadCsv(e, fields, form, 'Atualizações')
  }

  return (
    <header className={showSidebar ? 'margin-left' : ''}>
      <hgroup className="flex gap-16 align-center">
        {!showSidebar && <button
          type="button"
          className="toggle-menu-button"
          aria-label="Abrir menu de navegação"
          aria-controls="dashboard-sidebar"
          aria-expanded="false"
          data-dashboard-menu-trigger="project"
          onClick={() => openSidebar("project")}
        >
          <i
            className="icon icon-width-24 icon-angle-double-right background-accent"
            aria-hidden={true}
          ></i>
        </button>}
        <h1>{getHeader()}</h1>
        <PresentationModeBadge project={activeProject} />
        <PresentationMutationGate project={activeProject}>
        {modalVisible && <Modal close={handleModal}>
          <h3 className="update-title">ATUALIZAR DADOS</h3>
          <div className="close-icon" onClick={handleModal}>
            <i
              className="icon icon-width-24 icon-circle-x background-accent"
              aria-hidden={true}
            />
          </div>
          {activeProject.isActive ? <>
            <span className="update-building">{activeProject?.building_name}</span>
            {currentCompany?.updateLogs?.length > 0 && <>

              <div className="flex justify-content-space-between margin-top-16">
                <div>
                  <p>Última atualização da obra: <button style={{ color: "blue" }} onClick={handleSeeMoreBuilding}>Ver {seeAllBuildingUpdates ? "menos" : "tudo"}</button></p>

                </div>
                {seeAllBuildingUpdates && <button className="button button-accent small" onClick={(e) => handleDownload(e, 'building')}>Baixar relatório</button>}

              </div>
              <div className="historic">
                {seeAllBuildingUpdates ? getLastBuildingUpdate().map(i => <p>{i}</p>) : getLastBuildingUpdate()?.slice(0, 1).map(i => <p>{i}</p>)}
              </div>

              <div className="flex justify-content-space-between margin-top-16">
                <div>
                  <p>Últimas atualizações da construtora: <button style={{ color: "blue" }} onClick={handleSeeMoreCompany}>Ver {seeAllCompanyUpdates ? "menos" : "tudo"}</button></p>
                </div>
                {seeAllCompanyUpdates && <button className="button button-accent small" onClick={(e) => handleDownload(e, 'company')}>Baixar relatório</button>}
              </div>

              <div className="historic">
                {seeAllCompanyUpdates ? getCompanyUpdates().map(i => <p>{i}</p>) : getCompanyUpdates()?.slice(0, 3).map(i => <p>{i}</p>)}
              </div>
            </>}
            {/* Solicitação aceita COM painel de etapas na tela: o texto viraria repetição do que
                o painel já diz. Erro continua sempre visível. */}
            {response.done && !(response.ok && showStages) && <div className={`alert alert-${response.ok ? "success" : "error"} margin-top-32`} role="status" aria-live="polite">
              <h3 className="info-title">{response.responseHead}</h3>
              <p>{response.responseText}</p>
              {!response.ok && response.registrationLink && canOpenRegistration && (
                <Link className="update-action__link" to={BUILDING_REGISTRATION_PATH}>Abrir o cadastro da obra</Link>
              )}
            </div>}
            {/* Acompanhamento por etapas enquanto a execução está viva. As etapas vêm do
                data-pipeline (`update_jobs.stage`); o painel mantém uma consulta própria para
                não depender do polling que atualiza o rótulo do Header. */}
            {showStages && (
              <UpdateJobStages
                initialJob={currentJob}
                // O BE não repassa `sienge_quota_blocked_until` para dentro do job serializado
                // (só no topo do payload de status) — repassa aqui o valor que o laço de poll
                // deste Header já capturou, para a mensagem de cota sair completa no painel.
                quotaBlockedUntil={quotaBlockedUntil}
                // O painel consulta sozinho, de propósito: o laço abaixo é frágil (ver comentário
                // lá) e já congelou o acompanhamento em produção com o pipeline ainda rodando.
                fetchJob={async () => {
                  const res = await getUpdateJob(activeProject?.building_id)
                  return { ok: !!res?.ok, job: res?.body?.job ?? null }
                }}
                // Última etapa do fluxo é "Atualizando a tela": recarrega para os gráficos e
                // números refletirem os dados novos.
                onTerminal={(job) => {
                  setTerminalJobId(job.id)
                  if (job.status === 'succeeded') {
                    window.location.reload()
                    return
                  }
                  setResponse(previous => ({ ...previous, done: false }))
                }}
              />
            )}

            {!showStages && currentJob && updateJobMessage(currentJob, quotaBlockedUntil) && (
              <div className={`alert alert-${updateAlertVariant(currentJob)} margin-top-32`}>
                <h3 className="info-title">
                  {updateInProgress ? "Atualização em andamento" : "Última atualização"}
                </h3>
                <p>{updateJobMessage(currentJob, quotaBlockedUntil)}</p>
              </div>
            )}
            {/* Cota diária da API Sienge (Task 14): banner explicando o bloqueio + botões
                desabilitados com a mesma explicação no tooltip, enquanto blocked_until > agora. */}
            {quotaBlocked && (
              <div className="alert alert-error margin-top-24" role="status" aria-live="polite">
                <p>{formatSiengeQuotaMessage(quotaBlockedUntil)}</p>
              </div>
            )}
            {hasVisibleUpdateType && <p className="update-actions-label">Atualizações disponíveis</p>}
            <div className="update-actions">
              {!hasVisibleUpdateType && (
                <p className="empty-state">Nenhum tipo de atualização está disponível para esta obra.</p>
              )}
              {/* Com a lista de títulos anulados pendente o Financeiro vem primeiro e obrigatório. */}
              {annulmentsPending && supportsUpdate("financial") && (
                <div className="update-action update-action--required" data-required="true">
                  <button className="button button-accent update-action__button" title={quotaBlocked ? formatSiengeQuotaMessage(quotaBlockedUntil) : undefined} onClick={() => sendToUpdate("financial")} disabled={requestingUpdate || updateInProgress || (response.ok && response.done) || quotaBlocked}>Financeiro</button>
                  <div className="update-action__body">
                    <span className="update-action__badge update-action__badge--required">Obrigatório</span>
                    <p className="update-action-description">{updateActionDescription("financial", { annulmentsPending })}</p>
                    <p className="update-action-description update-action-required-reason" role="note">{ANNULMENTS_FINANCIAL_REASON}</p>
                  </div>
                </div>
              )}
              {supportsUpdate("complete") && (
                <div className="update-action">
                  <button className="button button-accent update-action__button" title={quotaBlocked ? formatSiengeQuotaMessage(quotaBlockedUntil) : undefined} onClick={() => sendToUpdate("complete")} disabled={requestingUpdate || updateInProgress || (response.ok && response.done) || quotaBlocked}>Completo{activeProject?.hasStock ? " (sem estoque)" : ""}</button>
                  <p className="update-action-description">{updateActionDescription("complete", { hasStock: activeProject?.hasStock, annulmentsPending })}</p>
                </div>
              )}
              {supportsUpdate("trend") && (
                <div className="update-action">
                  <button className="button button-accent update-action__button" title={quotaBlocked ? formatSiengeQuotaMessage(quotaBlockedUntil) : undefined} onClick={() => sendToUpdate("trend")} disabled={requestingUpdate || updateInProgress || (response.ok && response.done) || quotaBlocked}>Tendência</button>
                  <p className="update-action-description">{updateActionDescription("trend", { annulmentsPending })}</p>
                </div>
              )}
              {!annulmentsPending && supportsUpdate("financial") && (
                <div className="update-action">
                  <button className="button button-accent update-action__button" title={quotaBlocked ? formatSiengeQuotaMessage(quotaBlockedUntil) : undefined} onClick={() => sendToUpdate("financial")} disabled={requestingUpdate || updateInProgress || (response.ok && response.done) || quotaBlocked}>Financeiro</button>
                  <p className="update-action-description">{updateActionDescription("financial")}</p>
                </div>
              )}
              {supportsUpdate("purchase_orders") && (
                <div className="update-action update-action--with-period">
                  <button
                    className="button button-accent update-action__button"
                    onClick={sendRecentPurchaseOrdersUpdate}
                    disabled={requestingUpdate || updateInProgress || (response.ok && response.done) || quotaBlocked || !!purchaseOrdersPeriodInvalid}
                    title={quotaBlocked ? formatSiengeQuotaMessage(quotaBlockedUntil) : purchaseOrdersPeriodInvalid || undefined}
                  >
                    {purchaseOrdersLabel(currentCompany?.erp)}
                  </button>
                  <div className="update-action__body">
                    <p className="update-action-description">
                      {updateActionDescription("purchase_orders", {
                        erp: currentCompany?.erp,
                        purchaseOrdersPeriod: purchaseOrdersWholeWork ? WHOLE_WORK_PERIOD : purchaseOrdersPeriod,
                        wholeWorkStart,
                        annulmentsPending,
                      })}
                    </p>
                    {!purchaseOrdersFullHistory && (
                      <div className="update-action__scope" role="radiogroup" aria-label="Período dos pedidos">
                        <label>
                          <input
                            type="radio"
                            name="purchase-orders-scope"
                            value="window"
                            checked={!purchaseOrdersWholeWork}
                            onChange={() => setPurchaseOrdersScope("window")}
                            disabled={requestingUpdate || updateInProgress || quotaBlocked}
                          />
                          Período
                        </label>
                        <label>
                          <input
                            type="radio"
                            name="purchase-orders-scope"
                            value="whole_work"
                            checked={purchaseOrdersWholeWork}
                            onChange={() => setPurchaseOrdersScope("whole_work")}
                            disabled={!wholeWorkStart || requestingUpdate || updateInProgress || quotaBlocked}
                            aria-describedby={wholeWorkStart ? undefined : "purchase-orders-whole-work-reason"}
                          />
                          Obra inteira
                        </label>
                      </div>
                    )}
                    {!purchaseOrdersFullHistory && !wholeWorkStart && (
                      <p
                        id="purchase-orders-whole-work-reason"
                        className="update-action-description update-action-unavailable"
                        role="note"
                      >
                        Obra inteira indisponível: cadastre a data de início da obra.
                      </p>
                    )}
                    {/* Período ajustável (até 92 dias, mesmo teto do back-end) só no Sienge,
                        cuja API depende de uma janela real. A obra MEGA não pede período nem
                        oferece "Buscar todo o histórico": toda execução já cobre o histórico
                        inteiro de pedidos e contratos. */}
                    {!purchaseOrdersFullHistory && !purchaseOrdersWholeWork && (
                      <label className="update-action__period">
                        De
                        <input
                          type="date"
                          value={purchaseOrdersPeriod.start_date}
                          onChange={handlePurchaseOrdersPeriodChange("start_date")}
                          disabled={requestingUpdate || updateInProgress || quotaBlocked}
                        />
                        até
                        <input
                          type="date"
                          value={purchaseOrdersPeriod.end_date}
                          onChange={handlePurchaseOrdersPeriodChange("end_date")}
                          disabled={requestingUpdate || updateInProgress || quotaBlocked}
                        />
                      </label>
                    )}
                    {purchaseOrdersPeriodInvalid && (
                      <p className="update-action-description update-action-unavailable" role="alert">
                        {purchaseOrdersPeriodInvalid}
                      </p>
                    )}
                  </div>
                </div>
              )}
              {/* Obra MEGA cujo cadastro não tem CNPJ/projeto_reduzido: a ação não é oferecida
                  (evita disparar um run que já se sabe que falha no recorte — ver `_scope.py`),
                  mas o motivo aparece no lugar dela, em vez de só sumir sem explicação. */}
              {!supportsUpdate("purchase_orders") && purchaseOrdersUnavailable && (
                <div className="update-action update-action--unavailable">
                  <span className="update-action__badge">Indisponível</span>
                  <div className="update-action__body">
                    <p className="update-action-description update-action-unavailable" role="note">
                      {missingRegistrationMessage(purchaseOrdersUnavailable.fields)
                        ?? purchaseOrdersUnavailable.message}
                    </p>
                    {/* Atalho para o cadastro só para quem tem acesso ao Backoffice (D12). */}
                    {purchaseOrdersUnavailable.code === "missing_registration_fields" && canOpenRegistration && (
                      <Link className="update-action__link" to={BUILDING_REGISTRATION_PATH}>
                        Abrir o cadastro da obra
                      </Link>
                    )}
                  </div>
                </div>
              )}
              {activeProject?.hasStock && supportsUpdate("stock") && (
                <div className="update-action">
                  <button className="button button-accent update-action__button" title={quotaBlocked ? formatSiengeQuotaMessage(quotaBlockedUntil) : undefined} onClick={() => sendToUpdate("stock")} disabled={requestingUpdate || updateInProgress || (response.ok && response.done) || quotaBlocked}>Estoque</button>
                  <p className="update-action-description">{updateActionDescription("stock", { annulmentsPending })}</p>
                </div>
              )}
              {canRefreshPrevision(activeProject) && supportsUpdate("prevision") && (
                <div className="update-action">
                  <button
                    className="button button-accent update-action__button"
                    onClick={() => sendToUpdate("prevision")}
                    disabled={requestingUpdate || updateInProgress || (response.ok && response.done) || quotaBlocked}
                    title={quotaBlocked ? formatSiengeQuotaMessage(quotaBlockedUntil) : undefined}
                  >
                    Prevision
                  </button>
                  <p className="update-action-description">{updateActionDescription("prevision", { annulmentsPending })}</p>
                </div>
              )}
            </div>
          </> : <p style={{ fontWeight: 'bold', color: 'black' }}>Para atualizar a obra, faça o upgrade de plano.</p>}
        </Modal>}
        </PresentationMutationGate>
        {(!pathname.includes("bim") && !pathname.includes("costs")) && <div className="header-left-container">
          <div className="lang-selector">
            <label className="switch btn-color-mode-switch">
              <input
                type="checkbox"
                name="color_mode"
                id="color_mode"
                value="1"
                onChange={() => updateCubBased(!cubBased)}
              />
              <label
                htmlFor="color_mode"
                data-on="CUB"
                data-off="R$"
                className="btn-color-mode-switch-inner"
              ></label>
            </label>
          </div>
          {hasBothPlanBases && <div className="lang-selector" title="Comparar o IDP e a curva contra a linha de base ou o previsto da fonte selecionada">
            <label className="switch btn-color-mode-switch plan-basis-switch">
              <input
                type="checkbox"
                name="plan_basis"
                id="plan_basis"
                value="1"
                checked={forecastBased}
                onChange={() => updateForecastBased(!forecastBased)}
              />
              <label
                htmlFor="plan_basis"
                data-on="Previsto"
                data-off="Base"
                className="btn-color-mode-switch-inner"
              ></label>
            </label>
          </div>}
          <PresentationMutationGate project={activeProject}>
          {!checkIfMultipleIds() && <button disabled={requestingUpdate} onClick={handleModal} className="update-button" title={updateInProgress ? "Acompanhar atualização em andamento" : `Última atualização: ${getLastBuildingUpdate()?.slice(0, 1)[0]?.slice(0, 21)}`}>
            {updateInProgress || requestingUpdate ? "ATUALIZANDO" : "ATUALIZAR"}
          </button>}
          </PresentationMutationGate>
          {updatePollIssue && updateInProgress && (
            <span className="header-update-status" role="status" aria-live="polite">
              Não foi possível consultar o andamento agora. Tentaremos novamente; abra
              “ATUALIZANDO” para acompanhar.
            </span>
          )}

          <button className="toggle-offcanvas" onClick={toggleOffcanvas}>
            <i
              className="icon icon-width-16 icon-ellipsis-v"
              aria-hidden={true}
            ></i>
          </button>
          <button className="open-helper-button" onClick={toggleHelpModal}>?</button>
        </div>}

      </hgroup>
      <p className="headerLastUpdate">{user?.configurations?.header?.lastUpdate && `Última atualização em: ${getLastBuildingUpdate()?.slice(0, 1)[0]?.slice(0, 21)}`}</p>

    </header>
  );
};

export default Header;
